/**
 * Web Push client plumbing for the Settings → Notifications tab.
 *
 * The permission prompt MUST ride a user gesture: `enablePush` is called
 * only from the toggle tap (Chrome quiet-UI and Safari both require it).
 * iOS speciality: push exists only inside an installed Home-Screen web app
 * (16.4+) — `isIosSafariNotInstalled` powers the "install first" guidance
 * instead of a dead toggle.
 */

import { swReady } from './sw'

export type PushDeviceRow = {
  id: string
  endpoint: string
  user_agent: string | null
  created_at: string
  last_seen_at: string
  last_success_at: string | null
}

export type PushSubscriptionInfo = {
  vapid_public_key: string | null
  subscriptions: PushDeviceRow[]
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** Installed to the home screen (Android WebAPK or iOS Add-to-Home-Screen). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS Safari exposes navigator.standalone; everything modern supports the
  // display-mode media query (our manifest is display: standalone).
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true
  )
}

function isIosBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  // iPadOS 13+ reports as "Macintosh" but keeps multi-touch.
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
  )
}

/**
 * On an iPhone/iPad but NOT installed to the Home Screen — push can't work
 * here yet; the tab shows install guidance instead of a dead toggle.
 */
export function isIosSafariNotInstalled(): boolean {
  return isIosBrowser() && !isStandalone()
}

/** Notification.permission, SSR-safe. */
export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** The current browser's push subscription endpoint, when one exists. */
export async function currentSubscriptionEndpoint(): Promise<string | null> {
  if (!isPushSupported()) return null
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return null
  const sub = await registration.pushManager.getSubscription()
  return sub?.endpoint ?? null
}

/**
 * Register the SW, request permission (user gesture!), subscribe, and hand
 * the subscription to the caller (which POSTs it with its authed fetch).
 * Throws Error('permission_denied') when the user declines.
 */
export async function subscribeThisDevice(
  vapidPublicKey: string,
): Promise<{ endpoint: string; keys: { p256dh: string; auth: string } }> {
  // src/lib/sw.ts owns the ONE registration (it carries the `?v` build id) —
  // registering '/sw.js' here again would split update behavior across two
  // registration URLs. swReady() rejects immediately with the real error if
  // registration failed at boot (no hang), and simply waits out a slow first
  // install (the toggle already shows its busy state).
  const registration = await swReady()

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('permission_denied')

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
  })
  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('subscription_incomplete')
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  }
}

/**
 * Unsubscribe this browser locally; returns the endpoint that was released
 * so the caller can DELETE its server row. Null when nothing was subscribed.
 */
export async function unsubscribeThisDevice(): Promise<string | null> {
  const registration = await navigator.serviceWorker.getRegistration()
  const sub = await registration?.pushManager.getSubscription()
  if (!sub) return null
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  return endpoint
}
